import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ClerkProvider, useUser } from '@clerk/react';
import { Route, Switch, Router as WouterRouter, useLocation } from 'wouter';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Layout } from '@/components/layout';
import { ErrorBoundary } from '@/components/error-boundary';
import { AnalysisNotificationProvider } from '@/hooks/use-analysis-notifications';
import { useUtmAttribution } from '@/hooks/use-utm-attribution';
import { useReferralAttribution } from '@/hooks/use-referral';
import LandingPage from '@/pages/landing-page';
import DashboardPage from '@/pages/dashboard-page';
import MarketsPage from '@/pages/markets-page';
import MarketDetailPage from '@/pages/market-detail-page';
import AnalysesPage from '@/pages/analyses-page';
import PortfolioPage from '@/pages/portfolio-page';
import AlertsPage from '@/pages/alerts-page';
import PicksPage from '@/pages/picks-page';
import SettingsPage from '@/pages/settings-page';
import WatchlistPage from '@/pages/watchlist-page';
import AnalysisPublicPage from '@/pages/analysis-public-page';
import PrivacyPage from '@/pages/privacy-page';
import TermsPage from '@/pages/terms-page';
import NotFound from '@/pages/not-found';
import { useAnalyticsIdentify } from '@/hooks/use-analytics-identify';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function HomePage() {
  const { isSignedIn } = useUser();
  return isSignedIn ? <DashboardPage /> : <LandingPage />;
}

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { isSignedIn, isLoaded } = useUser();
  if (!isLoaded) return null;
  return isSignedIn ? <Component /> : <LandingPage />;
}

/** Set of exact paths served by the app shell. */
const SHELL_PATHS = new Set([
  '/',
  '/markets',
  '/picks',
  '/analyses',
  '/watchlist',
  '/portfolio',
  '/alerts',
  '/settings',
]);

/**
 * Three-tier routing:
 *   1. /share/:token  → no shell (public share page)
 *   2. known paths    → Layout shell + inner Switch
 *   3. everything else → shell-free 404
 *
 * Using useLocation() for the outer tier decision avoids any ambiguity
 * around Wouter's prefix vs. exact matching on "/".
 */
function AppRoutes() {
  const [location] = useLocation();

  // Persist first-touch UTM attribution to the Clerk user on sign-in.
  useUtmAttribution();
  // Attribute a captured referral code to the referrer, once, after sign-in.
  useReferralAttribution();

  // Identify signed-in users to PostHog and detect fresh signups.
  useAnalyticsIdentify();

  // Tier 1: share and legal pages are shell-free
  if (/^\/share\/[^/]/.test(location)) {
    return <AnalysisPublicPage />;
  }
  if (location === '/privacy') {
    return <PrivacyPage />;
  }
  if (location === '/terms') {
    return <TermsPage />;
  }

  // Tier 2: known paths get the app shell
  const isShellPath =
    SHELL_PATHS.has(location) ||
    // /markets/:id — any sub-path under /markets/
    /^\/markets\/[^/]/.test(location);

  if (!isShellPath) {
    // Tier 3: 404, no shell
    return <NotFound />;
  }

  return (
    <Layout>
      <Switch>
        <Route path="/" component={HomePage} />
        <Route path="/markets" component={MarketsPage} />
        <Route path="/markets/:id" component={MarketDetailPage} />
        <Route path="/picks" component={PicksPage} />
        <Route path="/analyses">
          <ProtectedRoute component={AnalysesPage} />
        </Route>
        <Route path="/watchlist">
          <ProtectedRoute component={WatchlistPage} />
        </Route>
        <Route path="/portfolio">
          <ProtectedRoute component={PortfolioPage} />
        </Route>
        <Route path="/alerts">
          <ProtectedRoute component={AlertsPage} />
        </Route>
        <Route path="/settings">
          <ProtectedRoute component={SettingsPage} />
        </Route>
      </Switch>
    </Layout>
  );
}

function App() {
  const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

  if (!clerkPubKey) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Configuration Error
          </h1>
          <p className="text-muted-foreground">
            Missing VITE_CLERK_PUBLISHABLE_KEY environment variable
          </p>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ClerkProvider
        publishableKey={clerkPubKey}
        proxyUrl={import.meta.env.PROD ? "/api/__clerk" : undefined}
      >
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, '')}>
              <AnalysisNotificationProvider>
                <AppRoutes />
              </AnalysisNotificationProvider>
            </WouterRouter>
            <Toaster />
          </TooltipProvider>
        </QueryClientProvider>
      </ClerkProvider>
    </ErrorBoundary>
  );
}

export default App;
